import { useEffect, useState } from 'react';
import { debounce, noop, uniqBy } from 'lodash';
import {
  useAreFeatureFlagsLoading,
  useFeatureFlagEnabled,
} from '@frameio/feature-flags';
import type {
  FolderAsset,
  Project,
  SuggestedAccountUser,
  SuggestedAccountUserGroup,
  SuggestedMember,
  SuggestedShareList,
  Workspace,
} from '@frameio/graphql-client/generated';
import { useGetSuggestedMembersQuery } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';

type MatchedMembers = Array<
  SuggestedAccountUser | SuggestedAccountUserGroup | SuggestedShareList
>;

const defaultMatches: MatchedMembers = [];
const defaultSuggestedMembers: SuggestedMember[] = [];

type OptionsType = {
  count?: number;
  debounceTimeMs?: number;
  resourceId?: FolderAsset['id'] | Project['id'] | Workspace['id'];
  prefetch?: boolean;
  skip?: boolean;
};

export const useSuggestedMemberSearch = ({
  count = DEFAULT_INITIAL_PAGE_SIZE,
  debounceTimeMs = 150,
  resourceId,
  prefetch,
  skip = false,
}: OptionsType) => {
  const areFeatureFlagsLoading = useAreFeatureFlagsLoading();
  const hideSuggestedMembers = useFeatureFlagEnabled(
    'v4.invites.hide_suggested_members'
  );
  const shouldSkipSuggestedMembers =
    skip || Boolean(areFeatureFlagsLoading) || Boolean(hideSuggestedMembers);
  const [mostRecentSearchQuery, setMostRecentSearchQuery] = useState('');
  const [allMembers, setAllMembers] = useState<Array<SuggestedMember>>([]);

  const {
    data,
    previousData,
    error,
    loading: isLoading,
  } = useGetSuggestedMembersQuery({
    variables: {
      filter: {
        query: mostRecentSearchQuery,
      },
      orderBy: [
        {
          field: 'NAME',
          direction: 'ASC_NULLS_LAST',
        },
      ],
      page: {
        first: count,
      },
      /**
       * It's okay to assert on the type of `resourceId` because we skip
       * the query if it's falsy.
       */
      resourceId: resourceId as string,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    skip: shouldSkipSuggestedMembers || !prefetch || !resourceId,
  });

  const pageInfo = data?.suggestedMembers?.pageInfo;
  const matches = data?.suggestedMembers?.nodes;

  const previousMatches = previousData?.suggestedMembers?.nodes;

  useEffect(
    () =>
      matches &&
      setAllMembers((prev) =>
        uniqBy([...prev, ...(matches as unknown as SuggestedMember[])], 'id')
      ),
    [matches]
  );

  const totalCount = (data ?? previousData)?.suggestedMembers?.totalCount;

  /**
   * Run this effect only on prefetch prop change rather than on sendQuery and
   * mostRecentSearchQuery change.
   */
  useEffect(() => {
    if (prefetch && !shouldSkipSuggestedMembers) {
      setMostRecentSearchQuery(mostRecentSearchQuery);
    }
  }, [mostRecentSearchQuery, prefetch, shouldSkipSuggestedMembers]);

  // @note(FIND-1871): Changed this to perform the search every time so that
  // we can display members who already have access in the list @edvinbesic
  const debouncedHandleQueryChange = debounce(
    (query: string) => setMostRecentSearchQuery(query),
    debounceTimeMs
  );

  useEffect(() => {
    return () => {
      debouncedHandleQueryChange.cancel();
    };
  }, [debouncedHandleQueryChange]);

  const matchedMembers = shouldSkipSuggestedMembers
    ? defaultMatches
    : ((isLoading
        ? previousMatches ?? defaultMatches
        : matches ||
          previousMatches ||
          defaultMatches) as unknown as MatchedMembers);

  return {
    allMembers: shouldSkipSuggestedMembers
      ? defaultSuggestedMembers
      : allMembers,
    error,
    hasNextPage: shouldSkipSuggestedMembers ? undefined : pageInfo?.hasNextPage,
    isLoading: shouldSkipSuggestedMembers ? false : isLoading && !matches,
    matchedMembers,
    onQueryChange: shouldSkipSuggestedMembers
      ? noop
      : debouncedHandleQueryChange,
    totalCount: shouldSkipSuggestedMembers ? undefined : totalCount,
  };
};
